// Campaign-member picklist value lists (QA round 8, U4 / item 4) — the ONE
// shared source for the six `launcher_sfdc_campaign_members` fields that have
// a known, closed set of enumerated values: CM Status, Sales Tier, Product
// Tier, Plan Interval, Partner Type, Account Fit.
//
// HARDCODED-LIST SMELL, FLAGGED AND ACCEPTED (`.claude/team-conventions.md`
// "No Hardcoded Fallbacks for Config/DB Data"): the real source of truth for
// every one of these SHOULD be Salesforce picklist metadata
// (`CampaignMember.Status`, `Account.Sales_Tier__c`, `Account.Product_Tier__c`,
// `Account.Plan_Interval__c`, `Account.Partner_Type__c`,
// `Account.Account_Fit__c`). Terra mirrors no picklist metadata today — this
// module is the honest interim, not a real fix, and the intake for this round
// (plans/2026-09-22-playbooks-qa-round8-plan.md, TL;DR decision 4) accepts
// that gap explicitly rather than papering over it.
//
// Promoted here (`mini-apps/CLAUDE.md` "Promotion rules" — a value list used
// by more than one app is a behavior contract that must stay in sync, so it
// gets promoted at the 2nd copy rather than waiting for a 3rd) from:
//   - `mini-apps/launcher/components/waterfallTypes.ts#CONDITION_FIELDS`
//     (cm_status, sales_tier, product_tier, plan_interval, partner_type)
//   - `mini-apps/playhouse/components/PlayhousePage.tsx`'s `contactFilterFields`
//     (account_fit, account_sales_tier)
// and now also consumed by `mini-apps/playbooks/campaign-member-filter-fields.ts`.
//
// Frontend-safe: no zod, no core service imports, plain `as const` string
// tuples only — every one of Launcher, Playhouse and Playbooks' frontends
// imports this directly.
// QA round 8 Codex fixes, F12 — five real statuses found missing from the
// original nine (`mini-apps/campaign-member-filter-fields.ts`'s
// `allowCustomValue` doc explains why this gap doesn't 400 a saved value):
//   - 'Invited'   — Launcher's own DEFAULT status for a new campaign member
//     (`launcher/components/BulkUploadModal.tsx:106,255,268`,
//     `launcher/components/EventManagerPage.tsx:961`) — the single most
//     common status was missing from its own picklist.
//   - 'Approved', 'Cancelled', 'Waitlisted' —
//     `launcher/components/subtype-colors.ts:48-55` and the terra_signals
//     rank array (`prisma/schema/migrations/20260914001000_terra_signals_campaign_snapshot/migration.sql:184`).
//   - 'Met with' — the terra_signals rank array (same migration).
// Deliberately excluded: 'Removed' (`src/services/launcher-member-sync.ts:542`)
// is Terra's own sentinel for a member removed from a campaign, not a real
// Salesforce CampaignMember.Status value — every signal query filters it out,
// so offering it as a filter value here would let a rep write a condition
// that can never usefully match a row.
//
// Casing note (also a reason this list is suggestions-only, not closed):
// the terra_signals rank array spells this status 'Pending approval'
// (lowercase 'a'), this list spells it 'Pending Approval' — a real,
// unresolved split, deferred rather than silently "fixed" by picking one.
export const CM_STATUS_VALUES = [
  'Attended',
  'No-Show',
  'Registered',
  'Sent',
  'Declined',
  'Pending Approval',
  'Responded',
  'Viewed Replay',
  'On-Demand',
  'Invited',
  'Approved',
  'Cancelled',
  'Waitlisted',
  'Met with',
] as const;

export const SALES_TIER_VALUES = ['Tier 1', 'Tier 2', 'Tier 3', 'Tier 4'] as const;

export const PRODUCT_TIER_VALUES = [
  'Free',
  'Starter',
  'Explorer',
  'Pro',
  'Growth',
  'Launch',
  'Enterprise',
  'Legacy',
] as const;

export const PLAN_INTERVAL_VALUES = ['Annual', 'Monthly', 'Custom'] as const;

export const PARTNER_TYPE_VALUES = [
  'Investment Fund: PE',
  'Investment Fund: VC',
  'Investment Fund: Growth',
  'Consulting Firm: GSI',
  'Consulting Firm: Strategy',
  'Artisan',
  'Advanced Artisan',
  'Studio',
  'Elite Studio',
  'Technology',
  'Marketplace',
  'Community',
  'Other',
] as const;

export const ACCOUNT_FIT_VALUES = ['Best Fit', 'Good Fit', 'Moderate Fit', 'Low Fit'] as const;
